import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { reportInaccurateLoad } from 'apis/myRequestsService';
import { useToastStore } from 'store/toastStore';


// Define the props interface
interface ReportInaccurateLoadPopupProps {
  onClose: () => void;
  loadResponseId: number; // Add this prop to pass the loadResponseId
}

const ReportInaccurateLoadPopup: React.FC<ReportInaccurateLoadPopupProps> = ({ onClose, loadResponseId }) => {
  const [comment, setComment] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (): Promise<void> => {
    if (!loadResponseId) {
      setError('Invalid load ID.');
      return;
    }
  
    if (comment.trim() === '') {
      setError('Please enter a comment before submitting');
      return;
    }
  
    setError('');
    setLoading(true);
  
    try {
      await reportInaccurateLoad(loadResponseId, comment);
      useToastStore.getState().showToast("Report submitted successfully.", "success",3000);
      onClose();
    } catch (error) {useToastStore.getState().showToast("Failed to submit the report. Please try again.", "error",3000);
      setError('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden transform transition-all">
        <div className="px-6 py-4 flex justify-between items-center border-b border-gray-200">
          <div className="flex items-center text-grease-100">
            <AlertTriangle className="mr-2 text-[#FFBC00]" size={20} />
            <h3 className="text-lg font-semibold m-0">Report Inaccurate Load</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl font-bold border-none bg-transparent cursor-pointer focus:outline-none"
          >
            &times;
          </button>
        </div>
        
        <div className="p-6">
          <div className="mb-4">
            <label htmlFor="inaccuracy" className="block mb-2 text-sm font-medium text-gray-700">
              Please describe the inaccuracy:
            </label>
            <textarea
              id="inaccuracy"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Describe what was incorrect about the load information..."
              className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-y font-inherit focus:outline-none focus:border-[#FFBC00] focus:ring-2 focus:ring-[#FFBC00]/30 transition-all"
            />
            {error && <p className="text-red-500 text-sm mt-2 flex items-center"><AlertTriangle className="mr-1" size={14} />{error}</p>}
          </div>
        </div>
        
        <div className="flex justify-end px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg mr-3 text-sm border-none cursor-pointer transition-colors font-medium"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className={`text-white px-5 py-2 rounded-lg text-sm border-none cursor-pointer transition-colors font-medium ${loading ? 'bg-[#FFBC00]/60 cursor-not-allowed' : 'bg-[#FFBC00] hover:bg-[#e6a800]'}`}
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportInaccurateLoadPopup;
